import { ColumnDef, createColumnHelper } from "@tanstack/react-table";
import { ActionButton } from "@via/components/action-button/action-button";
import { GenericError } from "@via/components/error";
import Table, { TableSize } from "@via/components/table/table";
import TableContextMenu from "@via/components/table/table-context-menu";
import { BUTTONS, ERROR, ORGANIZATION_V2_TABLE } from "@via/utils/constants";
import { useState } from "react";
import NewOrganizationModal from "@via/pages/account/admin/organizations/new-organization-modal.tsx";
import OrganizationModal from "@via/pages/account/admin/organizations/organization-modal.tsx";
import { useGetOrganizations } from "@via/pages/account/admin/organizations/organizations.api.tsx";
import { GetUserOrganizationDetailsForAdminResponseBodyItem } from "common";
import { formatDateString } from "@via/utils/helpers";

const columnHelper =
  createColumnHelper<GetUserOrganizationDetailsForAdminResponseBodyItem>();

const Organizations = () => {
  const [isOrganizationModalOpen, setIsOrganizationModalOpen] = useState(false);
  const [isNewOrganizationModalOpen, setIsNewOrganizationModalOpen] =
    useState(false);
  const [editableOrganization, setEditableOrganization] =
    useState<GetUserOrganizationDetailsForAdminResponseBodyItem | null>(null);

  const {
    data: organizations,
    isLoading: isOrganizationsLoading,
    isError: isOrganizationsError,
  } = useGetOrganizations();

  const handleContextMenuAction = (
    optionName: string,
    organization: GetUserOrganizationDetailsForAdminResponseBodyItem
  ): void => {
    if (optionName === "Manage") {
      setEditableOrganization(organization);
      setIsOrganizationModalOpen(true);
    }
  };

  const columns = [
    columnHelper.accessor("id", {
      cell: info => info.getValue(),
      header: () => <span>Id</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("name", {
      cell: info => info.getValue(),
      header: () => <span>Name</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("users", {
      // Zero is a real and useful answer here, not an empty state: an organization nobody belongs
      // to is exactly the one that needs somebody attached to it.
      cell: info => <span>{(info.getValue() ?? []).length}</span>,
      header: () => <span>Members</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("margin", {
      cell: info => {
        const val = info.getValue();
        if (val === null || val === undefined) {
          return <span>N/A</span>;
        }
        return <span>{val.toFixed(2)}%</span>;
      },
      header: () => <span>Margin</span>,
      enableSorting: false,
    }),
    columnHelper.accessor("createdAt", {
      cell: info => {
        const value = info.getValue();
        return <span>{formatDateString(value, "LLL")}</span>;
      },
      header: () => <span>Created At</span>,
      enableSorting: true,
    }),
    columnHelper.accessor("id", {
      header: () => "Options",
      cell: ({ row }) => (
        <TableContextMenu
          options={ORGANIZATION_V2_TABLE}
          onSelect={option => {
            handleContextMenuAction(option.name, row.original);
          }}
        />
      ),
      enableSorting: false,
    }),
  ] as Array<ColumnDef<GetUserOrganizationDetailsForAdminResponseBodyItem>>;

  return (
    <div className="p-6 shadow-sm ring-1 ring-gray-900/5 sm:mx-0 sm:w-full sm:rounded-lg">
      <div className="via-page-heading-wrapper">
        <h2 className="via-h2">Organizations</h2>
        <ActionButton
          button={BUTTONS.NEW_ORGANIZATION}
          primary={true}
          onClick={() => setIsNewOrganizationModalOpen(true)}
        />
      </div>

      {isOrganizationsError ? (
        <div className="via-generic-error">
          <GenericError message={ERROR} />
        </div>
      ) : (
        <>
          <Table
            isLoading={isOrganizationsLoading}
            data={organizations || []}
            columns={columns}
            total={(organizations || []).length}
            size={TableSize.Big}
          />
        </>
      )}
      {isOrganizationModalOpen && (
        <OrganizationModal
          organization={editableOrganization}
          isOpen={isOrganizationModalOpen}
          setIsOpen={setIsOrganizationModalOpen}
        />
      )}
      {isNewOrganizationModalOpen && (
        <NewOrganizationModal
          isOpen={isNewOrganizationModalOpen}
          setIsOpen={setIsNewOrganizationModalOpen}
        />
      )}
    </div>
  );
};

export default Organizations;
